import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/common/BackButton';

const SUPPORT_PHONE     = '9500869770';
const SUPPORT_WA        = `https://wa.me/91${SUPPORT_PHONE}?text=Hi Zeva2 Support, I need help with...`;
const REPORT_CATEGORIES = ['Bug / App Issue', 'Account Problem', 'Payment Issue', 'Crop / Listing Issue', 'Other'];

export default function SupportPage() {
  const navigate        = useNavigate();
  const { user }        = useAuth();
  const [tab, setTab]   = useState('home');
  const [faqs, setFaqs]             = useState([]);
  const [openFaq, setOpenFaq]       = useState(null);
  const [faqLoading, setFaqLoading] = useState(false);
  const [category, setCategory]     = useState(REPORT_CATEGORIES[0]);
  const [submitting, setSubmitting] = useState(false);

  const messageRef = useRef(null);

  useEffect(() => {
    if (tab === 'faq') loadFaqs();
  }, [tab]);

  const loadFaqs = async () => {
    setFaqLoading(true);
    setOpenFaq(null);
    // Use logged-in user's role — seller maps to 'seller', buyer→'buyer', agent→'agent'
    const role = user?.role === 'seller' ? 'seller'
               : user?.role === 'buyer'  ? 'buyer'
               : user?.role === 'agent'  ? 'agent'
               : 'all';
    try {
      const { data } = await api.get(`/support/faqs?role=${role}`);
      setFaqs(data.faqs || []);
    } catch {
      // Static fallback based on role
      const FALLBACK = {
        seller: [
          { _id:'s1', question:'How do I add a crop?',          answer:'Go to My Crops → tap "+ Add" → fill crop name, category, photos → tap "Add Crop".' },
          { _id:'s2', question:'How do I add stage updates?',    answer:'Open your crop → tap "+ Add Stage" → add photos and description of current growth.' },
          { _id:'s3', question:'How do I mark crop ready to sell?', answer:'Open My Crops → tap the status badge → select "Ready to Sell".' },
          { _id:'s4', question:'How do buyers contact me?',      answer:'Once marked "Ready to Sell", buyers can see your mobile and call you directly.' },
          { _id:'s5', question:'How do I delete a crop?',        answer:'Open crop detail → Manage Crop → tap Delete → confirm.' },
          { _id:'s6', question:'What is a referral code?',       answer:'Share your referral link from Profile. Friends who join via your link count as referrals.' },
        ],
        buyer: [
          { _id:'b1', question:'How do I find crops to buy?',    answer:'Go to Marketplace from bottom navigation. Filter by category and location.' },
          { _id:'b2', question:'How do I contact a farmer?',     answer:'Open any crop listing → scroll down → tap "Call Farmer" to call directly.' },
          { _id:'b3', question:'How do I request a specific crop?', answer:'Open a crop listing → tap "Request This Crop" → fill quantity and requirements.' },
          { _id:'b4', question:'What is the contact limit?',     answer:'Free plan allows limited contacts. Upgrade to Daily (₹99) or Monthly (₹499) for more.' },
          { _id:'b5', question:'How do I see my requests?',      answer:'Tap "Requests" in bottom navigation → My Requests tab.' },
          { _id:'b6', question:'What is a referral code?',       answer:'Share your referral link from Profile. Friends who join via your link count as referrals.' },
        ],
        agent: [
          { _id:'a1', question:'What does an agent do?',         answer:'Agents connect farmers and buyers. View all crop listings and buyer requests to facilitate deals.' },
          { _id:'a2', question:'How do I see incoming requests?', answer:'Tap "Requests" → Incoming Requests tab to see all buyer requests.' },
          { _id:'a3', question:'How do I contact farmers?',      answer:'Open any crop listing → tap "Call Farmer" to contact directly.' },
          { _id:'a4', question:'How do I earn from referrals?',  answer:'Share your referral link from Profile. When new users join using your link, referral count increases.' },
          { _id:'a5', question:'What is the contact limit?',     answer:'Upgrade to Daily (₹99) or Monthly (₹499) plan for more contacts.' },
        ],
        all: [
          { _id:'g1', question:'How do I update my profile?',    answer:'Go to Profile → tap "Edit Profile" → update name, location, gender → "Save Changes".' },
          { _id:'g2', question:'What is a referral code?',       answer:'Share your referral link from Profile. Friends who join via your link count as referrals.' },
          { _id:'g3', question:'How do I install the app?',      answer:'Open in Chrome → tap browser menu (⋮) → "Add to Home Screen".' },
        ],
      };
      setFaqs(FALLBACK[role] || FALLBACK.all);
    } finally { setFaqLoading(false); }
  };

  const submitReport = async () => {
    const message = messageRef.current?.value?.trim() || '';
    if (!message) return toast.error('Please describe your problem');
    if (message.length < 10) return toast.error('Please provide more details (min 10 chars)');
    setSubmitting(true);
    try {
      await api.post('/support/report', {
        category, message,
        userId: user?._id,
        mobile: user?.mobile,
        name:   user?.name,
      });
      toast.success("Report submitted! We'll get back to you. ✅");
      if (messageRef.current) messageRef.current.value = '';
      setTab('home');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit. Try WhatsApp instead.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="page">
      <div className="header header--simple">
        <BackButton onClick={() => tab === 'home' ? navigate(-1) : setTab('home')} />
        <div className="header__title">
          {tab === 'home' ? 'Support' : tab === 'report' ? '🐛 Report a Problem' : '❓ FAQ'}
        </div>
      </div>

      <div className="container" style={{ paddingTop:'var(--sp-4)', paddingBottom:'var(--sp-8)' }}>

        {/* ── Home ── */}
        {tab === 'home' && (
          <div>
            <div style={{ background:'linear-gradient(135deg, #1A6B3C, #2d9b5a)', borderRadius:16, padding:'20px 16px', marginBottom:20, textAlign:'center', color:'white' }}>
              <div style={{ fontSize:40, marginBottom:8 }}>🎧</div>
              <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>How can we help?</div>
              <div style={{ fontSize:13, opacity:0.85 }}>We're here to support you</div>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <a href={SUPPORT_WA} target="_blank" rel="noreferrer"
                style={{ display:'flex', alignItems:'center', gap:14, padding:'16px', background:'white', borderRadius:14, border:'1.5px solid #25D366', textDecoration:'none', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ width:48, height:48, borderRadius:12, background:'#25D366', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>📲</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:'#1a2e05', fontSize:15 }}>WhatsApp Support</div>
                  <div style={{ fontSize:12, color:'#64748B', marginTop:2 }}>Chat with us instantly</div>
                  <div style={{ fontSize:12, color:'#25D366', fontWeight:600, marginTop:3 }}>+91 {SUPPORT_PHONE}</div>
                </div>
                <span style={{ color:'#25D366', fontSize:20 }}>›</span>
              </a>

              <a href={`tel:+91${SUPPORT_PHONE}`}
                style={{ display:'flex', alignItems:'center', gap:14, padding:'16px', background:'white', borderRadius:14, border:'1.5px solid #3B82F6', textDecoration:'none', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ width:48, height:48, borderRadius:12, background:'#EFF6FF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>📞</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:'#1a2e05', fontSize:15 }}>Call Support</div>
                  <div style={{ fontSize:12, color:'#64748B', marginTop:2 }}>Mon–Sat, 9AM–6PM</div>
                  <div style={{ fontSize:12, color:'#3B82F6', fontWeight:600, marginTop:3 }}>+91 {SUPPORT_PHONE}</div>
                </div>
                <span style={{ color:'#3B82F6', fontSize:20 }}>›</span>
              </a>

              <button onClick={() => setTab('report')}
                style={{ display:'flex', alignItems:'center', gap:14, padding:'16px', background:'white', borderRadius:14, border:'1.5px solid #EF4444', cursor:'pointer', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ width:48, height:48, borderRadius:12, background:'#FEF2F2', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>🐛</div>
                <div style={{ flex:1, textAlign:'left' }}>
                  <div style={{ fontWeight:700, color:'#1a2e05', fontSize:15 }}>Report a Problem</div>
                  <div style={{ fontSize:12, color:'#64748B', marginTop:2 }}>Bug, account or listing issue</div>
                </div>
                <span style={{ color:'#EF4444', fontSize:20 }}>›</span>
              </button>

              <button onClick={() => setTab('faq')}
                style={{ display:'flex', alignItems:'center', gap:14, padding:'16px', background:'white', borderRadius:14, border:'1.5px solid #F59E0B', cursor:'pointer', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ width:48, height:48, borderRadius:12, background:'#FFFBEB', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>❓</div>
                <div style={{ flex:1, textAlign:'left' }}>
                  <div style={{ fontWeight:700, color:'#1a2e05', fontSize:15 }}>FAQ</div>
                  <div style={{ fontSize:12, color:'#64748B', marginTop:2 }}>Frequently asked questions</div>
                </div>
                <span style={{ color:'#F59E0B', fontSize:20 }}>›</span>
              </button>
            </div>

            <div style={{ textAlign:'center', marginTop:24, color:'#94A3B8', fontSize:12 }}>
              Zeva2 · Version 1.0.0 · Fresh. Direct. Trusted.
            </div>
          </div>
        )}

        {/* ── Report ── */}
        {tab === 'report' && (
          <div>
            <div className="card" style={{ padding:16, marginBottom:16 }}>
              <div className="form-group">
                <div>
                  <label className="label">Category *</label>
                  <select className="input" value={category}
                    onChange={e => setCategory(e.target.value)}>
                    {REPORT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div>
                  <label className="label">Describe the Problem *</label>
                  {/* Uncontrolled textarea using ref — no re-render on typing */}
                  <textarea
                    ref={messageRef}
                    rows={6}
                    placeholder="Please describe the issue in detail. What happened? What were you trying to do?"
                    style={{
                      width:'100%', padding:'10px 12px', borderRadius:8,
                      border:'1.5px solid #E2E8F0', fontSize:14, lineHeight:1.6,
                      resize:'vertical', minHeight:120, outline:'none',
                      fontFamily:'inherit', boxSizing:'border-box',
                    }}
                    onFocus={e => e.target.style.borderColor = '#1A6B3C'}
                    onBlur={e => e.target.style.borderColor = '#E2E8F0'}
                  />
                </div>

                {user && (
                  <div style={{ background:'#F8FAFC', borderRadius:10, padding:'10px 12px', border:'1px solid #E2E8F0' }}>
                    <div style={{ fontSize:11, color:'#64748B', marginBottom:4 }}>Submitting as:</div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#1a2e05' }}>{user.name || 'User'} · +91 {user.mobile}</div>
                  </div>
                )}
              </div>
            </div>

            <button className="btn btn--primary btn--lg" onClick={submitReport} disabled={submitting}>
              {submitting
                ? <><span className="spinner spinner--sm spinner--white" /> Submitting...</>
                : '📤 Submit Report'}
            </button>

            <div style={{ textAlign:'center', marginTop:16, fontSize:13, color:'#64748B' }}>
              Or contact us directly on{' '}
              <a href={SUPPORT_WA} target="_blank" rel="noreferrer" style={{ color:'#25D366', fontWeight:600 }}>WhatsApp</a>
            </div>
          </div>
        )}

        {/* ── FAQ ── */}
        {tab === 'faq' && (
          <div>
            {faqLoading ? (
              <div className="full-center"><span className="spinner spinner--lg" /></div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {faqs.map((faq, i) => (
                  <div key={faq._id || i}
                    style={{ background:'white', borderRadius:12, border:`1.5px solid ${openFaq===i ? '#1A6B3C' : '#E2E8F0'}`, overflow:'hidden' }}>
                    <button onClick={() => setOpenFaq(openFaq===i ? null : i)}
                      style={{ width:'100%', padding:'14px 16px', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                      <div style={{ fontSize:14, fontWeight:600, color:'#1a2e05', textAlign:'left', flex:1 }}>
                        {faq.question}
                      </div>
                      <span style={{ fontSize:16, color:'#1A6B3C', transition:'transform 0.2s', transform: openFaq===i ? 'rotate(180deg)' : 'none', flexShrink:0 }}>▼</span>
                    </button>
                    {openFaq === i && (
                      <div style={{ padding:'0 16px 14px', fontSize:13, color:'#475569', lineHeight:1.6, borderTop:'1px solid #F0FDF4' }}>
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
                {faqs.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-state__icon">❓</div>
                    <div className="empty-state__title">No FAQs yet</div>
                    <div className="empty-state__desc">Contact us on WhatsApp for help</div>
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop:20, padding:'14px', background:'#F0FDF4', borderRadius:12, textAlign:'center', border:'1px solid #BBF7D0' }}>
              <div style={{ fontSize:13, color:'#1A6B3C', fontWeight:600, marginBottom:6 }}>Still need help?</div>
              <a href={SUPPORT_WA} target="_blank" rel="noreferrer"
                style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', background:'#25D366', color:'white', borderRadius:20, fontSize:13, fontWeight:600, textDecoration:'none' }}>
                📲 Chat on WhatsApp
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}